import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { useMuestrasPermissions } from '../../components/muestras/LayoutMuestras';
import { getMuestrasBase, createMuestraBase, updateMuestraBase, deleteMuestraBase, getTelas, getEntalles, getTiposProducto, getMarcas, getHilos } from '../../services/muestrasApi';
import { toast } from 'sonner';
import { Layers, Plus, Edit2, Trash2, X, Upload, CheckCircle2, XCircle, Clock, Search, Download, AlertTriangle, Filter, RotateCcw } from 'lucide-react';
import { FileCell, RentabilidadBadge, StatusBadge } from '../../components/muestras/TableCells';
import { useResizableColumns, ResizableHeader } from '../../components/muestras/ResizableTable';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Definir columnas con anchos iniciales
const INITIAL_COLUMNS = [
  { key: 'id', header: 'ID', width: 60, minWidth: 50 },
  { key: 'tipo', header: 'Tipo', width: 120, minWidth: 80 },
  { key: 'entalle', header: 'Entalle', width: 110, minWidth: 80 },
  { key: 'tela', header: 'Tela', width: 140, minWidth: 100 },
  { key: 'marca', header: 'Marca', width: 110, minWidth: 80 },
  { key: 'costo', header: 'Costo', width: 100, minWidth: 70 },
  { key: 'precio', header: 'Precio', width: 100, minWidth: 70 },
  { key: 'archivo', header: 'Archivo', width: 120, minWidth: 80 },
  { key: 'estado', header: 'Estado', width: 90, minWidth: 70 },
  { key: 'acciones', header: 'Acciones', width: 100, minWidth: 80 },
];

const MuestrasMuestraBase = () => {
  const { user, token } = useApp();
  // Usar el nuevo sistema de permisos
  const { canCreate, canEdit, canDelete, isAdmin } = useMuestrasPermissions();
  
  // Permisos específicos para Muestras Base
  const puedeCrear = canCreate('muestras');
  const puedeEditar = canEdit('muestras');
  const puedeEliminar = canDelete('muestras');
  
  // Hook para columnas redimensionables
  const { columnWidths, handleResize, resetWidths } = useResizableColumns(INITIAL_COLUMNS, 'muestras-base');
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('aprobados');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    id_tipo: '', id_entalle: '', id_tela: '', id_marca: '', id_hilo: '',
    consumo_estimado: '', costo_estimado: '', precio_estimado: '', aprobado: 0
  });
  const [archivoFile, setArchivoFile] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [telas, setTelas] = useState([]);
  const [entalles, setEntalles] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [hilos, setHilos] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [muestrasData, telasData, entallesData, tiposData, marcasData, hilosData] = await Promise.all([
        getMuestrasBase(), getTelas(), getEntalles(), getTiposProducto(), getMarcas(), getHilos()
      ]);
      setData(muestrasData || []);
      setTelas(telasData || []);
      setEntalles(entallesData || []);
      setTipos(tiposData || []);
      setMarcas(marcasData || []);
      setHilos(hilosData || []);
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // Filtrado de datos
  const filteredData = useMemo(() => {
    let result = data;
    
    // Filtro por estado
    if (filter === 'aprobados') {
      result = result.filter(item => item.aprobado === 1);
    } else if (filter === 'pendientes') {
      result = result.filter(item => !item.aprobado || item.aprobado === 0);
    }
    
    // Filtro por búsqueda
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item => 
        item.codigo?.toLowerCase().includes(query) ||
        item.nombre?.toLowerCase().includes(query) ||
        item.marca_nombre?.toLowerCase().includes(query) ||
        item.tipo_nombre?.toLowerCase().includes(query) ||
        item.tela_nombre?.toLowerCase().includes(query) ||
        item.entalle_nombre?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [data, filter, searchQuery]);

  // Contadores para filtros
  const aprobadosCount = data.filter(item => item.aprobado === 1).length;
  const pendientesCount = data.filter(item => !item.aprobado || item.aprobado === 0).length;

  const openModal = (item = null) => {
    setEditingItem(item);
    setFormData({
      id_tipo: item?.id_tipo || '',
      id_entalle: item?.id_entalle || '',
      id_tela: item?.id_tela || '',
      id_marca: item?.id_marca || '',
      id_hilo: item?.id_hilo || '',
      consumo_estimado: item?.consumo_estimado || '',
      costo_estimado: item?.costo_estimado || '',
      precio_estimado: item?.precio_estimado || '',
      aprobado: item?.aprobado || 0
    });
    setArchivoFile(null);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.id_tipo || !formData.id_entalle || !formData.id_tela) {
      toast.error('Tipo, Entalle y Tela son requeridos');
      return;
    }
    try {
      let itemId;
      if (editingItem) {
        await updateMuestraBase(editingItem.id_muestra_base, formData);
        itemId = editingItem.id_muestra_base;
        toast.success('Muestra actualizada');
      } else {
        const result = await createMuestraBase(formData);
        itemId = result.id_muestra_base;
        toast.success('Muestra creada');
      }
      
      // Subir archivo si hay uno seleccionado
      if (archivoFile && itemId) {
        await handleUploadArchivo(itemId, archivoFile);
      }
      
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Error al guardar');
    }
  };

  const handleUploadArchivo = async (id, file) => {
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const uploadRes = await fetch(`${API_BASE}/api/muestras/upload?folder=muestras-base/costos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (!uploadRes.ok) {
        const errorData = await uploadRes.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error subiendo archivo');
      }
      
      const { filename } = await uploadRes.json();
      
      await updateMuestraBase(id, { archivo_costo: filename });
      return true;
    } catch (error) {
      console.error('Error en handleUploadArchivo:', error);
      throw error;
    }
  };

  const handleRemoveArchivo = async (id) => {
    await updateMuestraBase(id, { archivo_costo: null });
    loadData();
    toast.success('Archivo eliminado');
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await deleteMuestraBase(deleteItem.id_muestra_base);
      toast.success('Muestra eliminada');
      setDeleteItem(null);
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  // Calcular rentabilidad
  const calcRentabilidad = (costo, precio) => {
    if (!costo || !precio || costo === 0) return null;
    return ((precio - costo) / costo) * 100;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full" data-testid="muestras-base-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-100 rounded-xl">
            <Layers className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Muestras Base</h1>
            <p className="text-gray-400 text-sm">Gestión de muestras base</p>
          </div>
        </div>
        {puedeCrear && (
          <button 
            onClick={() => openModal()} 
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Nueva Muestra
          </button>
        )}
      </div>

      {/* Filtros y Búsqueda */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Filtros de estado */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <button
            onClick={() => setFilter('aprobados')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === 'aprobados' 
                ? 'bg-green-600 text-white' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-green-50'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            Aprobados ({aprobadosCount})
          </button>
          <button
            onClick={() => setFilter('pendientes')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === 'pendientes' 
                ? 'bg-slate-600 text-white' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-slate-50'
            }`}
          >
            <XCircle className="w-4 h-4" />
            Pendientes ({pendientesCount})
          </button>
          <button
            onClick={() => setFilter('todos')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === 'todos' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-blue-50'
            }`}
          >
            Todos ({data.length})
          </button>
        </div>

        {/* Búsqueda */}
        <div className="relative flex-1 max-w-xs ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por marca, tipo, tela..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Botón para resetear anchos */}
        <div className="px-4 py-2 bg-slate-50 border-b flex items-center justify-between">
          <span className="text-xs text-gray-500">Arrastra los bordes de las columnas para ajustar el ancho</span>
          <button
            onClick={resetWidths}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            title="Restaurar anchos por defecto"
          >
            <RotateCcw className="w-3 h-3" />
            Restaurar
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : filteredData.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No hay muestras base</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead className="bg-slate-100 border-b">
                <tr>
                  <ResizableHeader
                    width={columnWidths.id}
                    onResize={(w) => handleResize('id', w)}
                    minWidth={50}
                    className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase"
                  >
                    ID
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.tipo}
                    onResize={(w) => handleResize('tipo', w)}
                    minWidth={80}
                    className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase"
                  >
                    Tipo
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.entalle}
                    onResize={(w) => handleResize('entalle', w)}
                    minWidth={80}
                    className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase"
                  >
                    Entalle
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.tela}
                    onResize={(w) => handleResize('tela', w)}
                    minWidth={100}
                    className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase"
                  >
                    Tela
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.marca}
                    onResize={(w) => handleResize('marca', w)}
                    minWidth={80}
                    className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase"
                  >
                    Marca
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.costo}
                    onResize={(w) => handleResize('costo', w)}
                    minWidth={70}
                    className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase"
                  >
                    Costo
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.precio}
                    onResize={(w) => handleResize('precio', w)}
                    minWidth={70}
                    className="px-3 py-3 text-right text-xs font-semibold text-slate-600 uppercase"
                  >
                    Precio
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.archivo}
                    onResize={(w) => handleResize('archivo', w)}
                    minWidth={80}
                    className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase"
                  >
                    Archivo
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.estado}
                    onResize={(w) => handleResize('estado', w)}
                    minWidth={70}
                    className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase"
                  >
                    Estado
                  </ResizableHeader>
                  {(puedeEditar || puedeEliminar) && (
                    <ResizableHeader
                      width={columnWidths.acciones}
                      onResize={(w) => handleResize('acciones', w)}
                      minWidth={80}
                      className="px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase"
                    >
                      Acciones
                    </ResizableHeader>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => (
                  <tr key={item.id_muestra_base} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-3 font-mono text-sm text-gray-500 truncate" style={{ width: columnWidths.id }}>{item.id_muestra_base}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 truncate" style={{ width: columnWidths.tipo }}>{item.tipo_nombre || '-'}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 truncate" style={{ width: columnWidths.entalle }}>{item.entalle_nombre || '-'}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 font-medium truncate" style={{ width: columnWidths.tela }}>{item.tela_nombre || '-'}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 truncate" style={{ width: columnWidths.marca }}>{item.marca_nombre || '-'}</td>
                    <td className="px-3 py-3 font-mono text-sm text-right truncate" style={{ width: columnWidths.costo }}>
                      {item.costo_estimado ? `S/ ${parseFloat(item.costo_estimado).toFixed(2)}` : '-'}
                    </td>
                    <td className="px-3 py-3 font-mono text-sm text-right truncate" style={{ width: columnWidths.precio }}>
                      {item.precio_estimado ? `S/ ${parseFloat(item.precio_estimado).toFixed(2)}` : '-'}
                    </td>
                    <td className="px-3 py-3 text-center" style={{ width: columnWidths.archivo }}>
                      <FileCell
                        value={item.archivo_costo}
                        onUpload={async (file) => {
                          try {
                            await handleUploadArchivo(item.id_muestra_base, file);
                            loadData();
                            toast.success('Archivo subido');
                          } catch (error) {
                            toast.error(error.message || 'Error al subir archivo');
                          }
                        }}
                        onRemove={() => handleRemoveArchivo(item.id_muestra_base)}
                        accept=".pdf,.xlsx,.xls,.doc,.docx"
                        disabled={!puedeEditar}
                      />
                    </td>
                    <td className="px-3 py-3 text-center" style={{ width: columnWidths.estado }}>
                      <StatusBadge aprobado={item.aprobado} />
                    </td>
                    {(puedeEditar || puedeEliminar) && (
                      <td className="px-3 py-3" style={{ width: columnWidths.acciones }}>
                        <div className="flex items-center justify-center gap-1">
                          {puedeEditar && (
                            <button 
                              onClick={() => openModal(item)} 
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {puedeEliminar && (
                            <button 
                              onClick={() => setDeleteItem(item)} 
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Crear/Editar - Diseño Minimalista */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-xl shadow-2xl">
            <div className="px-6 py-5 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingItem ? 'Editar Muestra Base' : 'Nueva Muestra Base'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <div className="px-6 pb-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Tipo y Entalle */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de Producto *</label>
                  <select
                    value={formData.id_tipo || ''}
                    onChange={(e) => setFormData({ ...formData, id_tipo: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                  >
                    <option value="" className="text-gray-400">Seleccionar tipo</option>
                    {tipos.map(t => <option key={t.id_tipo} value={t.id_tipo}>{t.nombre_tipo}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Entalle *</label>
                  <select
                    value={formData.id_entalle || ''}
                    onChange={(e) => setFormData({ ...formData, id_entalle: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                  >
                    <option value="" className="text-gray-400">Seleccionar entalle</option>
                    {entalles.map(e => <option key={e.id_entalle} value={e.id_entalle}>{e.nombre}</option>)}
                  </select>
                </div>
              </div>

              {/* Tela y Marca */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Tela *</label>
                  <select
                    value={formData.id_tela || ''}
                    onChange={(e) => setFormData({ ...formData, id_tela: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                  >
                    <option value="" className="text-gray-400">Seleccionar tela</option>
                    {telas.map(t => <option key={t.id_tela} value={t.id_tela}>{t.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Marca</label>
                  <select
                    value={formData.id_marca || ''}
                    onChange={(e) => setFormData({ ...formData, id_marca: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                  >
                    <option value="" className="text-gray-400">Seleccionar marca</option>
                    {marcas.map(m => <option key={m.id_marca} value={m.id_marca}>{m.nombre_marca}</option>)}
                  </select>
                </div>
              </div>

              {/* Consumo y Costo */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Consumo Estimado</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.consumo_estimado}
                    onChange={(e) => setFormData({ ...formData, consumo_estimado: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                    placeholder=""
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Costo Estimado (S/)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.costo_estimado}
                    onChange={(e) => setFormData({ ...formData, costo_estimado: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                    placeholder=""
                  />
                </div>
              </div>

              {/* Precio y Rentabilidad */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Precio Estimado (S/)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.precio_estimado}
                    onChange={(e) => setFormData({ ...formData, precio_estimado: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-colors"
                    placeholder=""
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Rentabilidad Estimada</label>
                  <div className="px-3 py-2.5 border border-gray-100 rounded-lg bg-gray-50 text-gray-500 text-sm">
                    {formData.costo_estimado && formData.precio_estimado ? (
                      <RentabilidadBadge costo={parseFloat(formData.costo_estimado)} precio={parseFloat(formData.precio_estimado)} />
                    ) : (
                      <span>-</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Estado Aprobado */}
              <div className="flex items-center gap-3 py-1">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, aprobado: formData.aprobado === 1 ? 0 : 1 })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${formData.aprobado === 1 ? 'bg-blue-500' : 'bg-gray-200'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.aprobado === 1 ? 'translate-x-5' : ''}`} />
                </button>
                <span className="text-sm text-gray-700">Aprobado</span>
              </div>

              {/* Archivo de Costo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Archivo de Costo</label>
                {editingItem?.archivo_costo ? (
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Download className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        {editingItem.archivo_costo.split('/').pop()?.split('_').slice(1).join('_') || 'archivo'}
                      </p>
                      <p className="text-xs text-gray-400">Archivo actual</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={`${API_BASE}/api/muestras/files/${editingItem.archivo_costo}?download=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Descargar"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        type="button"
                        onClick={async () => {
                          if (window.confirm('¿Eliminar archivo de costo?')) {
                            await handleRemoveArchivo(editingItem.id_muestra_base);
                            setEditingItem({ ...editingItem, archivo_costo: null });
                          }
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar archivo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : archivoFile ? (
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Upload className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">{archivoFile.name}</p>
                      <p className="text-xs text-gray-400">Listo para subir</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setArchivoFile(null)}
                      className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf,.xlsx,.xls,.doc,.docx"
                      onChange={(e) => setArchivoFile(e.target.files[0])}
                      className="hidden"
                      id="archivo-costo"
                    />
                    <label htmlFor="archivo-costo" className="cursor-pointer">
                      <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Arrastra archivos aquí o haz clic para seleccionar</p>
                      <p className="text-xs text-gray-400 mt-1">Soporta: .pdf, .xlsx, .xls, .doc, .docx</p>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 flex justify-end gap-3">
              <button 
                onClick={() => setShowModal(false)} 
                className="px-5 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handleSubmit} 
                className="px-5 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium transition-colors"
              >
                {editingItem ? 'Guardar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Eliminar Muestra Base?</h3>
              <p className="text-gray-500 mb-6">
                ¿Estás seguro de eliminar la muestra base <strong>#{deleteItem.id_muestra_base}</strong>? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button 
                  onClick={() => setDeleteItem(null)} 
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button 
                  onClick={confirmDelete} 
                  className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium disabled:opacity-50"
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MuestrasMuestraBase;
